/**
 * SKU 类型枚举、模板衍生字段（addTemplateType）与添加照片页编辑路由解析。
 * 类型数组集中在本模块；addTemplateType 末尾通过运行时 require(common) 读取环境，避免与 common 静态循环依赖。
 */
import { subType_POSTER_ARR, sticker_label_layout } from '@/utils/constant';
import { ROUTE_NAMES } from '@/utils/routeNames';
import * as editUtils from '@/utils/editUtil.js';
import { convertComponentDataToAcrylic } from '@/utils/serviceComponentHole/index.js';

// skuType: https://www.showdoc.com.cn/xingbai/11558553171262098
export const cardTypeList = [1]; // 证件照
export const pbTypeList = [4]; // 拼版冲印
export const lifeTypeList = [2]; // 生活照
export const nameStickerTypeList = [15]; // 姓名贴
export const pldSkyTypeList = [3, 9, 10, ...nameStickerTypeList]; // 拍立得
export const lifePbTypeList = [11, 12]; //  人生四格
export const keyTypeList = [18]; // 钥匙扣
export const retouchHeadTypeList = []; // 只抠头
export const pasterTypeList = [13, ...keyTypeList, ...retouchHeadTypeList]; // 异型抠图\钥匙扣
export const photoRollTypeList = [14]; // 照片卷
export const isCustomSizeTypeList = [16]; // 自定义尺寸
export const colorPaperTypeList = [19]; // 色纸
export const bajiTypeList = [20]; // 吧唧
export const beidianTypeList = [21]; // 亚克力杯垫
export const templateLayerTypeList = [22]; // 素材:有模板
export const noTemplateLayerTypeList = [23]; // 素材:无模板
export const crystalTypeList = [24]; // 水晶标
export const frontBackLayer = [99]; // 正反面图层
export const stickerTypeList = [25]; // 贴纸
export const templateMutiImage = [26]; // 模板多图
export const layerDataSkuTypeList = [...stickerTypeList, ...templateMutiImage]; //有layerData
export const layerTypeList = [
  22,
  ...crystalTypeList,
  ...noTemplateLayerTypeList,
  ...frontBackLayer
]; // 模板

export const supportDivideUploadList = [
  // 这里面的路由会直接从orderDetail跳转到addPhoto页面，如果不想要直接到addPhoto，就不把路由加进去
  ...lifeTypeList,
  ...pldSkyTypeList,
  ...lifePbTypeList,
  ...pasterTypeList,
  ...isCustomSizeTypeList,
  ...colorPaperTypeList,
  ...bajiTypeList,
  ...beidianTypeList,
  ...layerTypeList,
  ...layerDataSkuTypeList
]; // 支持拆分上传的类型

let mwxRuntimeCache;

/**
 * 运行期获取 common 模块（首调时 require，避免 skuManage 与 common 顶层互引）。
 * @returns {Object} common 模块命名空间
 */
function getMwxRuntime() {
  if (!mwxRuntimeCache) {
    mwxRuntimeCache = require('@/utils/common.js');
  }
  return mwxRuntimeCache;
}

/**
 * 根据 skuType / subType 及运行环境，为订单或商品 template 写入 isXxx 等衍生字段（就地修改）。
 * @param {Object} template - 接口返回的 template 对象
 * @returns {void}
 */
export function addTemplateType(template) {
  let mwx = getMwxRuntime();

  // 证件照
  template.isCard = cardTypeList.indexOf(template.skuType) > -1;
  // 拼版冲印
  template.isPb = pbTypeList.indexOf(template.skuType) > -1;
  // 拍立得
  template.isPld = pldSkyTypeList.indexOf(template.skuType) > -1;
  // 生活照
  template.isLife = lifeTypeList.indexOf(template.skuType) > -1;
  // 人生四格
  template.isLifePb = lifePbTypeList.indexOf(template.skuType) > -1;
  // 支持拆分上传
  template.isSupportDivideUpload =
    supportDivideUploadList.indexOf(template.skuType) > -1;
  // 抠图（异型抠图\钥匙扣\只抠头）
  template.isPaster = pasterTypeList.indexOf(template.skuType) > -1;
  // 钥匙扣
  template.isKeyHole = keyTypeList.indexOf(template.skuType) > -1;
  // 立牌
  template.isLiPai = !!template.slotWidth;
  // 照片卷
  template.isPhotoRoll = photoRollTypeList.indexOf(template.skuType) > -1;
  // 只抠头
  template.isRetouchHead = retouchHeadTypeList.indexOf(template.skuType) > -1;
  // 自定义尺寸
  template.isCustomSize = isCustomSizeTypeList.indexOf(template.skuType) > -1;
  // 色纸
  template.isColorPaper = colorPaperTypeList.indexOf(template.skuType) > -1;
  // 透明色纸\双闪色纸
  template.isPasterColorPaper =
    ['COLOR_ALPHA', 'COLOR_SHAN', 'COLOR_LAMINATED'].indexOf(template.subType) >
    -1;
  // 吧唧
  template.isBaji = bajiTypeList.indexOf(template.skuType) > -1;
  // 双闪吧唧
  template.isPasterBaji = ['BADGE_SHAN'].indexOf(template.subType) > -1;
  // 亚克力杯垫
  template.isBeidian = beidianTypeList.indexOf(template.skuType) > -1;
  // 新方案的sku, 基于 layerInfo
  template.isLayerSku = layerTypeList.indexOf(template.skuType) > -1;
  // 有模板的
  template.isTemplateLayerSku =
    templateLayerTypeList.indexOf(template.skuType) > -1;

  // layerSku: 没有模板选项
  template.isOutlineLayerSku =
    noTemplateLayerTypeList.indexOf(template.skuType) > -1;

  // layerSku: 钥匙扣
  template.isKeyHoleLayerSku =
    template.isOutlineLayerSku && ['HOLE'].indexOf(template.subType) > -1;

  // layerSku: 立牌
  template.isLiPaiLayerSku =
    template.isOutlineLayerSku && ['STAND'].indexOf(template.subType) > -1;

  // 自定义亚克力多组件
  template.isCustomYakeliSku =
    template.isOutlineLayerSku && ['COMPONENT'].indexOf(template.subType) > -1;

  // TODO
  if (mwx.isDev) {
    // template.borderType = 1;
  }

  // borderType!=2时,没有白墨,底色为白色
  template.isNoTranparent = template.borderType != 2;

  template.isFrontBackLayer = template.backEnabled; // 是否支持正反面不同图
  // 正反不同图禁用
  if (template.isFrontBackLayer) {
    template.defaultPage = null;
  }

  template.isGeneratePrototypeImage = template.mockupEnabled; // 是否显示样机图

  // 正反图: 多通道图片 逆向和局部UV、白墨
  template.isReverseLayerSku = template.duplexEnabled; // 是否支持反面图

  // 正反图: 无白墨\其他图层
  template.isReverseLayerNoOtherLayerSku =
    template.duplexEnabled && !template.multiChannel; //

  // 是否是水晶标
  template.isCrystalLayerSku = crystalTypeList.indexOf(template.skuType) > -1;

  // 铜版纸海报
  template.isColorPoster =
    subType_POSTER_ARR.indexOf(template.subType) > -1 ||
    template.laminationEnabled;

  template.isLayerDataSku =
    layerDataSkuTypeList.indexOf(template.skuType) > -1 || template.isLayerSku;

  // 是否显示透明网格
  template.isShowTransparentGrid =
    !template.isNoTranparent || template.isCrystalLayerSku;

  // 自由贴纸排版
  template.isAutoLayerPaperSKu = stickerTypeList.indexOf(template.skuType) > -1;

  let dyData = mwx.getDouyinData();
  let wxData = mwx.getWxData();
  let isDouyinApp = dyData && dyData.orderNo; // 是否在抖音小程序中
  let isH5ToMiniApp = !(wxData && wxData.token && wxData.itemId); // h5跳转小程序内下单上传
  //  有itemId说明是小程序内下单上传
  if (isDouyinApp || isH5ToMiniApp) {
    template.isGeneratePrototypeImage = 0;
    template.mockupEnabled = 0;
  }

  if (template.isReverseLayerSku || template.isReverseLayerNoOtherLayerSku) {
    template.isGeneratePrototypeImage = 1;
    template.mockupEnabled = 1;
  }
  // 自定义尺寸
  template.isCustomSkuSize = template.customize;

  // TODO
  if (mwx.isDev) {
    //
    // template.isCustomSkuSize = 1;
    // template.isKeyHoleLayerSku = true;
    // template.isLiPaiLayerSku = false;
    // template.subType = 'test';
    // template.isLiPai = false;
    // template.isCustomYakeliSku = true;
    template.isGeneratePrototypeImage = 1;
    template.mockupEnabled = 1;
    // template.isReverseLayerNoOtherLayerSku = 1;
    // template.defaultPage = mwx.constant.SkuTemplateDefaultPageMap.segment_body;
  }
  if (template.isAutoLayerPaperSKu) {
    // 贴纸类型： 画布总宽高 = 纸张宽高 + 出血边
    // template.paperWidth = template.paperWidth + template.shadow * 2;
    // template.paperHeight = template.paperHeight + template.shadow * 2;
    template.paperWidthWithBlood = template.paperWidth + template.shadow * 2; // 新增paperWidthWithShadow、paperHeightWithShadow 表示加上1出血后的纸张宽高
    template.paperHeightWithBlood = template.paperHeight + template.shadow * 2;
  }
  template.isTemplateMutiImage =
    templateMutiImage.indexOf(template.skuType) > -1; // 模板多图
}

function isEditPaster(template) {
  return (
    template.isPaster ||
    template.isBeidian ||
    ((template.isColorPaper || template.isBaji) &&
      (template.isPasterColorPaper || template.isPasterBaji))
  );
}

/**
 * 添加照片流程中，根据已带 isXxx 的 template 解析编辑页路由 name（与历史 if 链优先级一致）。
 * @param {Object} template - 已执行 addTemplateType 的 template
 * @returns {string} 对应 ROUTE_NAMES；不支持时返回空字符串
 */
export function getAddPhotoEditRouteName(template) {
  // 根据template 模板数据跳转决定addPhoto上传文件之后跳转的页面
  if (template.skuType == 2) {
    return ROUTE_NAMES.editLifePhoto;
  }
  if (template.isPld) {
    return ROUTE_NAMES.editPld;
  }
  if (template.isLifePb) {
    return ROUTE_NAMES.selectLifeTemplate;
  }
  if (isEditPaster(template)) {
    return ROUTE_NAMES.editPaster;
  }
  if (template.isCustomSize) {
    return ROUTE_NAMES.customSize;
  }
  if (template.isColorPaper || template.isBaji) {
    if (template.isPasterColorPaper || template.isPasterBaji) {
      return ROUTE_NAMES.editPaster;
    }
    return ROUTE_NAMES.editColorPaper;
  }
  if (template.isLayerSku) {
    return ROUTE_NAMES.edit;
  }
  if (template.subType === sticker_label_layout) {
    // 水晶标阵列先进入抠图页面
    return ROUTE_NAMES.stickerRetouchFromAdd;
  }
  if (stickerTypeList.indexOf(template.skuType) > -1) {
    return ROUTE_NAMES.stickerEdit;
  }
  if (templateMutiImage.indexOf(template.skuType) > -1) {
    // 模板多图
    return ROUTE_NAMES.templateMutiImageSelect;
  }
  let errMsg = `getAddPhotoEditRouteName 报错: 当前sku未指定编辑路由页面`;
  if (process.env.NODE_ENV === 'development') {
    alert(errMsg);
  } else {
    console.error(errMsg);
  }
  return '';
}

/**
 * addPhoto 重构：开发环境断言数据转换分支已覆盖。
 * @param {string} message - 错误描述
 * @param {Object} context - 辅助排查上下文
 * @returns {void}
 */
function assertAddPhotoConvertHandled(message, context) {
  let mwx = getMwxRuntime();
  if (!mwx.isDev) {
    return;
  }
  console.error(message, context);
  throw new Error(message);
}

/**
 * addPhoto 重构：获取用于排查转换分支的 template 关键字段。
 * @param {Object} template - SKU 模板
 * @returns {Object} template 关键字段
 */
function getTemplateDebugInfo(template) {
  template = template || {};
  return {
    skuType: template.skuType,
    subType: template.subType,
    isLife: template.isLife,
    isPld: template.isPld,
    isLifePb: template.isLifePb,
    isPaster: template.isPaster,
    isBeidian: template.isBeidian,
    isCustomSize: template.isCustomSize,
    isColorPaper: template.isColorPaper,
    isBaji: template.isBaji,
    isPasterColorPaper: template.isPasterColorPaper,
    isPasterBaji: template.isPasterBaji,
    isLayerSku: template.isLayerSku,
    isLayerDataSku: template.isLayerDataSku
  };
}

/**
 * addPhoto 重构：创建后端照片项转前端照片项的基础字段。
 * @param {Object} item - 后端照片项
 * @returns {Object} 前端照片项基础字段
 */
function createFrontendListItemBase(item) {
  return {
    ...item,
    status: 'resolve',
    count: item.qty
  };
}

/**
 * addPhoto 重构：转换人生四格后端照片项为前端格式。
 * @param {Object} item - 后端照片项
 * @returns {Object} 前端照片项
 */
function convertFront_LifePb(item) {
  return {
    ...createFrontendListItemBase(item),
    originImageSrc: item.imageUrlList[0]
  };
}

/**
 * addPhoto 重构：转换 layerData 后端照片项为前端格式。
 * @param {Object} item - 后端照片项
 * @returns {Object} 前端照片项
 */
function convertFront_LayerDataSku(item) {
  return {
    ...createFrontendListItemBase(item),
    ...editUtils.convertBackendToFrontEnd(item)
  };
}

/**
 * addPhoto 重构：转换普通后端照片项为前端格式。
 * @param {Object} item - 后端照片项
 * @param {Object} template - SKU 模板
 * @returns {Object} 前端照片项
 */
function convertFront_Normal(item, template) {
  let mwx = getMwxRuntime();
  let data = createFrontendListItemBase(item);
  data.originImageSrc = item.imageUrlList[0];
  if (!item.cropInfo && template.isPaster && item.imageInfo) {
    return data;
  }
  data.cropInfo = mwx.convertCropInfo(item.cropInfo, 'fontEnd');
  return data;
}

/**
 * addPhoto 重构：后端照片项转换为前端照片项。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 后端照片项
 * @param {Object} options.template - SKU 模板
 * @returns {Object} 前端照片项
 */
export function convertBackendItemToFrontendItem(options) {
  let item = options.item;
  let template = options.template || {};
  if (template.isLifePb) {
    return convertFront_LifePb(item);
  }
  if (template.isLayerDataSku) {
    return convertFront_LayerDataSku(item);
  }
  if (!item.imageUrlList || !item.imageUrlList.length) {
    assertAddPhotoConvertHandled('addPhoto 后端转前端缺少 imageUrlList', {
      template: getTemplateDebugInfo(template),
      item
    });
  }
  if (!item.cropInfo && !(template.isPaster && item.imageInfo)) {
    assertAddPhotoConvertHandled('addPhoto 后端转前端未匹配到明确分支', {
      template: getTemplateDebugInfo(template),
      item
    });
  }
  return convertFront_Normal(item, template);
}

/**
 * addPhoto 重构：后端列表转换为 addPhoto 前端列表。
 * @param {Object} options - 参数对象
 * @param {Array} options.list - 后端照片列表
 * @param {Object} options.template - SKU 模板
 * @returns {Object} 转换结果，包含 list 和需要回写页面的模板状态
 */
export function convertBackendListToFrontendList(options) {
  let list = options.list || [];
  let template = options.template || {};
  let result = {
    list: [],
    templatePatch: null,
    editRouteName: ''
  };
  result.list = list.map((item) => {
    if (!item.cropInfo && template.isPaster && item.imageInfo) {
      result.templatePatch = {
        isPaster: false,
        isLayerSku: true
      };
      result.editRouteName = ROUTE_NAMES.edit;
    }
    return convertBackendItemToFrontendItem({
      item,
      template
    });
  });
  return result;
}

/**
 * addPhoto 重构：创建前端照片项转后端照片项的基础字段。
 * @param {Object} item - 前端照片项
 * @param {Object} template - SKU 模板
 * @returns {Object} 后端照片项基础字段
 */
function createBaseSubmitItem(item, template) {
  let mwx = getMwxRuntime();
  let cropInfo = {};
  if (!template.isLifePb && item.cropInfo) {
    // 非人生四格，需要转换裁剪信息
    cropInfo = mwx.convertCropInfo(item.cropInfo, 'toServer');
  }
  if (!template.isCustomSize) {
    // 非自定义尺寸，需要添加纸张尺寸
    cropInfo = {
      ...cropInfo,
      paperWidth: template.paperWidth,
      paperHeight: template.paperHeight
    };
  }
  item.qty = item.count; // 确保count和qty一致
  return {
    qty: item.count,
    imageUrlList: [item.originImageSrc], //用户原图
    cropInfo
  };
}

/**
 * addPhoto 重构：转换拍立得前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 前端照片项
 * @param {Object} options.data - 后端照片项基础字段
 * @returns {Object} 后端照片项
 */
function convertBack_Pld(options) {
  let item = options.item;
  let data = options.data;
  data.polaroid = {
    templateId: item.polaroid.templateId,
    imageUrl: item.polaroid.imageUrl,
    rect: item.polaroid.rect,
    imageWidth: item.polaroid.imageWidth,
    imageHeight: item.polaroid.imageHeight
  };
  if (item.fontList && item.fontList.length > 0) {
    data.fontList = item.fontList;
  }
  data.previewUrl = item.previewUrl;
  return data;
}

/**
 * addPhoto 重构：转换生活照前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 前端照片项
 * @param {Object} options.data - 后端照片项基础字段
 * @param {Object} options.template - SKU 模板
 * @returns {Object} 后端照片项
 */
function convertBack_LifePhoto(options) {
  let item = options.item;
  let data = options.data;
  let template = options.template;
  if (item.fontList && item.fontList.length > 0) {
    data.fontList = item.fontList;
  }
  data.previewUrl = item.previewUrl;
  if (template.isColorPoster) {
    data.extra = item.extra;
  }
  return data;
}

/**
 * addPhoto 重构：转换异形贴纸前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 前端照片项
 * @param {Object} options.data - 后端照片项基础字段
 * @param {Object} options.template - SKU 模板
 * @param {Function} options.uploadLocalImage - 本地图片上传任务收集函数
 * @param {Array} options.uploadTempImageTask - 原图上传任务数组
 * @param {Array} options.keyHoleUploadImageArr - 钥匙扣预览图上传任务数组
 * @returns {Object} 后端照片项
 */
function convertBack_Paster(options) {
  let item = options.item;
  let data = options.data;
  let template = options.template;
  let uploadLocalImage = options.uploadLocalImage;
  let uploadTempImageTask = options.uploadTempImageTask;
  let keyHoleUploadImageArr = options.keyHoleUploadImageArr;
  data.fileName = item.fileName;
  data.border = item.border;
  data.previewUrl = item.previewUrl;
  data.extra = item.extra;
  data.acrylic = item.acrylic;

  if (uploadLocalImage) {
    uploadLocalImage({
      imageSrc: item.originImageSrc,
      imageSrcKey: 'originImageSrc',
      promiseArr: uploadTempImageTask
    });
  }

  // 吧唧和色纸的outlineImageSr不是预览图
  let falsePreviewUrl = template.isBaji || template.isColorPaper;
  if (
    item.drawCanvasInfo &&
    item.drawCanvasInfo.outlineImageSrc &&
    !falsePreviewUrl
  ) {
    data.previewUrl = item.drawCanvasInfo.outlineImageSrc;
    if (uploadLocalImage) {
      uploadLocalImage({
        imageSrc: data.previewUrl,
        imageSrcKey: 'previewUrl',
        promiseArr: keyHoleUploadImageArr
      });
    }
  }

  if (template.isKeyHole) {
    if (item.acrylic && item.acrylic.holes) {
      // 有孔位数据才上传
      if (item.acrylic.holes.length) {
        data.acrylic = item.acrylic;
        delete data.acrylic.previewImageUrl;
      }
    } else {
      data._isError = true;
    }
  }

  if (template.isLiPai) {
    let slotRect = item.acrylic && item.acrylic.slotRect;
    if (slotRect && Array.isArray(slotRect)) {
      let validCount = slotRect.filter(function (slotItem) {
        return slotItem !== null && slotItem !== undefined;
      }).length;
      if (validCount < 6) {
        data._isError = true;
      }
    } else {
      data._isError = true;
    }
  }

  if (
    item.shapeTemplateInfo &&
    item.shapeTemplateInfo.drawCanvasInfo &&
    item.shapeTemplateInfo.shapeOriginImageSrc
  ) {
    // 形状的原图(未裁剪)
    data.imageUrlList = [item.shapeTemplateInfo.shapeOriginImageSrc];
  }
  if (template.isPasterColorPaper) {
    data.extra = item.extra;
  }
  return data;
}

/**
 * addPhoto 重构：转换人生四格前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 前端照片项
 * @param {Object} options.data - 后端照片项基础字段
 * @returns {Object} 后端照片项
 */
function convertBack_LifePb(options) {
  let item = options.item;
  let data = options.data;
  if (item.puzzleInfo) {
    data.puzzleInfo = item.puzzleInfo;
  }
  return data;
}

/**
 * addPhoto 重构：转换色纸/吧唧前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 前端照片项
 * @param {Object} options.data - 后端照片项基础字段
 * @returns {Object} 后端照片项
 */
function convertBack_ColorPaper(options) {
  let item = options.item;
  let data = options.data;
  data.extra = item.extra;
  return data;
}

/**
 * addPhoto 重构：转换自定义尺寸前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.data - 后端照片项基础字段
 * @returns {Object} 后端照片项
 */
function convertBack_CustomSize(options) {
  return options.data;
}

/**
 * addPhoto 重构：转换 layerData 前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 前端照片项
 * @param {Object} options.data - 后端照片项基础字段
 * @param {Object} options.template - SKU 模板
 * @param {number} options.index - 照片项索引
 * @param {Array} options.editLayerImageInfoTasks - imageInfo 上传任务数组
 * @returns {Object} 后端照片项
 */
function convertBack_LayerDataSku(options) {
  let item = options.item;
  let data = options.data;
  let template = options.template;
  let index = options.index;
  let editLayerImageInfoTasks = options.editLayerImageInfoTasks;
  if (item.imageInfo && !item.layerData) {
    return item;
  }
  if (!item.layerData) {
    data._isError = true;
    return data;
  }
  let convertedData = editUtils.convertFrontendToBackend(
    item.layerData,
    template
  );
  data.imageInfo = convertedData.front; // 正面图
  if (template.isReverseLayerSku) {
    data.duplex = convertedData.back; // 反面
  }

  if (template.isKeyHoleLayerSku) {
    data.acrylic = {
      ...(data.acrylic || {}),
      holes: item.layerData.keyPoint
    };
  } else if (template.isLiPaiLayerSku) {
    let slotRect;
    if (
      item.layerData &&
      item.layerData.socketInfo &&
      item.layerData.socketInfo.slotRect
    ) {
      slotRect = item.layerData.socketInfo.slotRect;
      if (slotRect[4] === undefined) {
        let mwx = getMwxRuntime();
        let storeTemplate = mwx.Store.get(mwx.constant.skuTemplate) || {};
        slotRect[4] = storeTemplate.slotWidth;
        slotRect[5] = storeTemplate.slotHeight;
      }
    }
    data.acrylic = {
      ...(data.acrylic || {}),
      slotRect
    };
    if (!slotRect) {
      data._isError = true;
    }
  }
  if (template.isCustomYakeliSku) {
    let componentData = item.layerData.componentData;
    let acrylic = convertComponentDataToAcrylic(componentData);
    data.acrylic = {
      ...(data.acrylic || {}),
      ...acrylic
    };
  }
  data.extra = {
    ...(data.extra || {}),
    ...(item.extra || {}),
    mask: item.layerData.backendMaskUrl
  };
  data.border = item.layerData.strokeWidth;
  if (data.imageInfo && data.imageInfo.layers) {
    editLayerImageInfoTasks.push({
      index: index,
      imageInfo: data.imageInfo
    });
    data.imageInfo.maskUrl = item.layerData.maskUrl; // 模板多图保存时新增maskUrl、maskId，表示选中的蒙层
    data.imageInfo.maskId = item.layerData.maskId;
  }
  data.previewUrl = item.layerData.previewUrl;
  data.imageUrlList = [item.layerData.previewUrl];
  delete data.cropInfo;
  return data;
}

/**
 * addPhoto 重构：根据 template 获取前端转后端处理函数。
 * @param {Object} template - SKU 模板
 * @returns {Function|null} 处理函数
 */
function getConvertBackHandler(template) {
  if (template.isPld) {
    return convertBack_Pld;
  }
  if (template.isLife) {
    return convertBack_LifePhoto;
  }
  if (isEditPaster(template)) {
    return convertBack_Paster;
  }
  if (template.isLifePb) {
    return convertBack_LifePb;
  }
  if (template.isCustomSize) {
    return convertBack_CustomSize;
  }
  if (template.isColorPaper || template.isBaji) {
    return convertBack_ColorPaper;
  }
  if (template.isLayerDataSku) {
    return convertBack_LayerDataSku;
  }
  return null;
}

/**
 * addPhoto 重构：前端照片列表转换为后端提交列表。
 * @param {Object} options - 参数对象
 * @param {Array} options.list - 前端照片列表
 * @param {Object} options.template - SKU 模板
 * @param {Function} options.uploadLocalImage - 本地图片上传任务收集函数
 * @returns {Object} 转换结果，包含 list 和上传任务数组
 */
export function convertFrontendListToBackendList(options) {
  let list = options.list || [];
  let template = options.template || {};
  let uploadTempImageTask = [];
  let keyHoleUploadImageArr = [];
  let editLayerImageInfoTasks = [];
  let handler = getConvertBackHandler(template);
  if (!handler) {
    assertAddPhotoConvertHandled('addPhoto 前端转后端未匹配到转换分支', {
      template: getTemplateDebugInfo(template)
    });
  }
  return {
    list: list.map((item, index) => {
      let data = createBaseSubmitItem(item, template);
      if (!handler) {
        return data;
      }
      return handler({
        item,
        data,
        template,
        index,
        uploadLocalImage: options.uploadLocalImage,
        uploadTempImageTask,
        keyHoleUploadImageArr,
        editLayerImageInfoTasks
      });
    }),
    uploadTempImageTask,
    keyHoleUploadImageArr,
    editLayerImageInfoTasks
  };
}

/**
 * addPhoto 重构：回写提交转换后产生的上传任务结果，并标记错误数据。
 * @param {Object} options - 参数对象
 * @param {Array} options.list - 后端提交列表
 * @param {Object} options.template - SKU 模板
 * @param {Array} options.uploadTempImageTask - 原图上传任务数组
 * @param {Array} options.keyHoleUploadImageArr - 钥匙扣预览图上传任务数组
 * @param {Array} options.editLayerImageInfoTasks - editLayer 类型的 imageInfo 上传任务数组
 * @param {Function} options.uploadImageInfoLayers - imageInfo.layers 上传函数
 * @returns {Promise<Array>} 回写上传结果后的后端提交列表
 */
export async function applySubmitImageTaskResults(options) {
  let mwx = getMwxRuntime();
  let list = options.list || [];
  let template = options.template || {};
  let uploadTempImageTask = options.uploadTempImageTask || [];
  let keyHoleUploadImageArr = options.keyHoleUploadImageArr || [];
  let editLayerImageInfoTasks = options.editLayerImageInfoTasks || [];

  if (
    template.isPaster ||
    template.isBeidian ||
    ((template.isColorPaper || template.isBaji) &&
      (template.isPasterColorPaper || template.isPasterBaji))
  ) {
    if (uploadTempImageTask.length) {
      let arr = await Promise.all(uploadTempImageTask);
      list.forEach((item) => {
        let matchItem = arr.filter(
          (taskItem) => taskItem.originImageSrc == item.imageUrlList[0]
        )[0];
        if (matchItem) {
          if (matchItem.isError) {
            item._isError = true;
          } else {
            item.imageUrlList = [matchItem.imageSrc];
          }
        }
      });
    }

    try {
      if (keyHoleUploadImageArr.length) {
        let arr = await Promise.all(keyHoleUploadImageArr);
        list.forEach((item) => {
          let matchItem = arr.filter(
            (taskItem) => taskItem.previewUrl == item.previewUrl
          )[0];
          if (matchItem) {
            if (matchItem.isError) {
              item._isError = true;
            } else {
              item.previewUrl = matchItem.imageSrc;
            }
          }
        });
      }
    } catch (error) {
      mwx.log.error(error);
    }
  }

  if (
    template.isLayerSku &&
    editLayerImageInfoTasks.length > 0 &&
    options.uploadImageInfoLayers
  ) {
    try {
      let uploadPromises = editLayerImageInfoTasks.map((task) => {
        return options
          .uploadImageInfoLayers(task.imageInfo)
          .then((updatedImageInfo) => {
            if (list[task.index]) {
              list[task.index].imageInfo = updatedImageInfo;
            }
            return { index: task.index, imageInfo: updatedImageInfo };
          })
          .catch((item) => {
            if (list[item.index]) {
              list[item.index]._isError = true;
            }
          });
      });
      await Promise.all(uploadPromises);
    } catch (error) {
      mwx.log.error('uploadImageInfoLayers fail:', error);
    }
  }

  return list;
}
